import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ConfirmPasswordChange = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [tokenProcessed, setTokenProcessed] = useState(false);
  
  const { confirmPasswordChange, loading } = useAuth();

  // Automatically process token on component mount
  useEffect(() => {
    const processToken = async () => {
      if (!token) {
        setResult({
          type: 'error',
          message: 'No confirmation token provided. Please check your email for the confirmation link.'
        });
        return;
      }

      if (tokenProcessed) return;

      setIsProcessing(true);
      setTokenProcessed(true);

      try {
        const response = await confirmPasswordChange(token);
        
        if (response.success) {
          setResult({
            type: 'success',
            message: response.message || 'Your password has been changed successfully! You can now log in with your new password.'
          });
          
          // Redirect to login after a delay
          setTimeout(() => {
            navigate('/', { state: { showLogin: true, message: 'Password changed successfully. Please log in with your new password.' } });
          }, 3000);
        } else {
          setResult({
            type: 'error',
            message: response.error || 'Password change confirmation failed. The token may be invalid or expired.'
          });
        }
      } catch (error) {
        setResult({
          type: 'error',
          message: 'An unexpected error occurred while confirming your password change. Please try again.'
        });
      } finally {
        setIsProcessing(false);
      }
    };

    processToken();
  }, [token, confirmPasswordChange, tokenProcessed, navigate]);

  const goToHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">Password Change Confirmation</h2>
          <p className="mt-2 text-sm text-gray-600">
            Confirming your password change
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          
          {/* Processing State */}
          {isProcessing && (
            <div className="text-center">
              <div className="animate-spin mx-auto h-8 w-8 text-videbits-600">
                <svg fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="mt-4 text-gray-600">Processing your password change...</p>
            </div>
          )}

          {/* Result Messages */}
          {result && (
            <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
              result.type === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              {result.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              )}
              <p className={`text-sm ${
                result.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {result.message}
              </p>
            </div>
          )}

          {/* Success Action */}
          {result?.type === 'success' && (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Redirecting to login page in 3 seconds...
              </p>
              <button
                onClick={() => navigate('/', { state: { showLogin: true } })}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-videbits-600 hover:bg-videbits-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-videbits-500"
              >
                Go to Login Now
              </button>
            </div>
          )}

          {/* Back to Home */}
          <div className={result ? "mt-6" : ""}>
            <button
              onClick={goToHome}
              className="w-full flex items-center justify-center space-x-2 text-videbits-600 hover:text-videbits-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmPasswordChange;